import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getCookie, setCookie, extractEmailFromUserIdCookie } from '@/src/utils/browser'
import { RootState } from '../rootReducer'

export interface UserProfile {
    firstName?: string
    lastName?: string
    fullName?: string
    picture?: string
    mobile?: string
    countryCode?: string
    displayName?: string
}

export interface AuthState extends UserProfile {
    signedIn: boolean
    email?: string
    userId?: string | number
    ctAuth?: string
}

/**
 * Safely parses user data from cookies
 */
const parseUserData = (userData: string | null): Partial<UserProfile> => {
    if (!userData) return {}

    try {
        return JSON.parse(decodeURIComponent(userData))
    } catch (error) {
        console.error('Error parsing user data from cookie:', error)
        return {}
    }
}

/**
 * Extracts user ID from the userid cookie
 */
const extractUserId = (userIdCookie: string | null): string | undefined => {
    if (!userIdCookie) return undefined

    const idMatch = userIdCookie.match(/\|(\d+)\|/)
    return idMatch?.[1]
}

/**
 * Gets the initial auth state from cookies (client-side only)
 */
const getAuthStateFromCookies = (): AuthState => {
    const usermisc = getCookie('usermisc')
    const userid = getCookie('userid')
    const userData = getCookie('userData')

    // If not signed in, return default state
    if (!usermisc || !usermisc.includes('SIGNED_IN')) {
        return { signedIn: false }
    }

    // Parse user data from cookies
    const userInfo = parseUserData(userData || null)
    const email = userid ? extractEmailFromUserIdCookie(userid) : undefined
    const userId = userid ? extractUserId(userid) : (userInfo as { userId?: string | number })?.userId

    return {
        signedIn: true,
        email,
        userId,
        ...userInfo
    }
}

// Initialize from cookies on client-side, use default on server to prevent hydration mismatch
const initialState: AuthState = typeof window !== 'undefined' ? getAuthStateFromCookies() : { signedIn: false }

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        /**
         * Updates the auth state with new user data and persists displayName to cookie
         */
        setAuthUser: (state, action: PayloadAction<Partial<AuthState>>) => {
            const newState = { ...state, ...action.payload }

            // Save displayName to userData cookie for persistence across page reloads
            if (typeof window !== 'undefined' && newState.fullName) {
                try {
                    const domain = window.location.hostname
                    setCookie(
                        'userData',
                        encodeURIComponent(JSON.stringify({ displayName: newState.fullName })),
                        365,
                        domain
                    )
                } catch (error) {
                    console.error('Error saving user data to cookie:', error)
                }
            }

            return newState
        },

        /**
         * Clears the auth state (on sign out) and removes userData cookie
         */
        clearAuthUser: () => {
            // Clear userData cookie on sign out
            if (typeof window !== 'undefined') {
                try {
                    const domain = '.' + window.location.hostname
                    setCookie('userData', '', -1, domain) // Set expired cookie to delete it
                } catch (error) {
                    console.error('Error clearing userData cookie:', error)
                }
            }

            return {
                signedIn: false,
                email: undefined,
                userId: undefined,
                firstName: undefined,
                lastName: undefined,
                fullName: undefined,
                picture: undefined,
                mobile: undefined,
                countryCode: undefined,
                displayName: undefined
            }
        },

        /**
         */
        refreshAuthUser: () => getAuthStateFromCookies()
    }
})

// Action creators
export const { setAuthUser, clearAuthUser, refreshAuthUser } = authSlice.actions

// Selectors
export const selectAuth = (state: RootState) => state.auth
export const selectIsAuthenticated = (state: RootState) => state.auth.signedIn
export const selectCurrentUser = (state: RootState) => ({
    email: state.auth.email,
    userId: state.auth.userId,
    fullName: state.auth.fullName,
    firstName: state.auth.firstName,
    lastName: state.auth.lastName,
    picture: state.auth.picture
})

export default authSlice.reducer
